interface GeminiInlineData {
  mimeType?: string;
  mime_type?: string;
  data: string;
}

interface GeminiPart {
  inlineData?: GeminiInlineData;
  inline_data?: GeminiInlineData;
}

interface GeminiImageResponse {
  candidates?: { content?: { parts?: unknown[] } }[];
}

/** Lato lungo massimo (px) inviato al modello: l'output richiesto è 1K. */
const MAX_IMAGE_DIMENSION = 1024;
const DOWNSCALE_JPEG_QUALITY = 0.92;

/**
 * Riduce l'immagine al lato lungo massimo prima dell'invio: le foto da
 * smartphone sono spesso 4000px+/svariati MB, ma l'output richiesto è 1K.
 * Ridimensionare client-side abbatte payload, latenza e costo. In caso di
 * errore (o immagine già piccola) si ripiega sul file originale.
 */
export async function toUploadPayload(
  file: File
): Promise<{ data: string; mimeType: string }> {
  const fallbackMime = file.type || 'image/jpeg';
  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: 'from-image',
    });
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= MAX_IMAGE_DIMENSION) {
      bitmap.close();
      return { data: await blobToBase64(file), mimeType: fallbackMime };
    }

    const scale = MAX_IMAGE_DIMENSION / longest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return { data: await blobToBase64(file), mimeType: fallbackMime };
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, DOWNSCALE_JPEG_QUALITY)
    );
    if (!blob) {
      return { data: await blobToBase64(file), mimeType: fallbackMime };
    }
    return { data: await blobToBase64(blob), mimeType };
  } catch {
    return { data: await blobToBase64(file), mimeType: fallbackMime };
  }
}

export function extractImageFromResponse(response: GeminiImageResponse): Blob {
  const parts = response.candidates?.[0]?.content?.parts ?? [];
  for (const part of parts as GeminiPart[]) {
    const inline = part.inlineData ?? part.inline_data;
    if (inline?.data) {
      const mime = inline.mimeType ?? inline.mime_type ?? 'image/jpeg';
      return base64ToBlob(inline.data, mime);
    }
  }
  throw new Error('No image returned from Gemini');
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
